import React, { useState, useMemo, useEffect } from 'react';
import { X, Search, Download } from 'lucide-react';

const PAGE_SIZE = 100;

// key -> { title, columns: [{label, field, kind?}] }
const CONFIGS = {
  blocks: {
    title: 'Blocks',
    columns: [
      { label: 'District',    field: 'district', stateOnly: true },
      { label: 'Block',       field: 'block' },
      { label: 'Villages',    field: 'villages' },
      { label: 'Habitations', field: 'habitations' },
      { label: 'SC Pop',      field: 'sc' },
      { label: 'SC %',        field: 'sc_pct' },
      { label: 'ST Pop',      field: 'st' },
      { label: 'ST %',        field: 'st_pct' },
      { label: 'General',     field: 'general' },
      { label: 'Gen %',       field: 'general_pct' },
      { label: 'Total Pop',   field: 'total' },
    ],
  },
  top3_sc: {
    title: 'High Concentration SC Blocks — Top 3',
    columns: [
      { label: 'Rank',        field: 'rank' },
      { label: 'District',    field: 'district', stateOnly: true },
      { label: 'Block',       field: 'block' },
      { label: 'Villages',    field: 'villages' },
      { label: 'Habitations', field: 'habitations' },
      { label: 'SC Pop',      field: 'sc' },
      { label: 'SC %',        field: 'sc_pct' },
      { label: 'ST Pop',      field: 'st' },
      { label: 'ST %',        field: 'st_pct' },
      { label: 'Total Pop',   field: 'total' },
    ],
  },
  top3_st: {
    title: 'High Concentration ST Blocks — Top 3',
    columns: [
      { label: 'Rank',        field: 'rank' },
      { label: 'District',    field: 'district', stateOnly: true },
      { label: 'Block',       field: 'block' },
      { label: 'Villages',    field: 'villages' },
      { label: 'Habitations', field: 'habitations' },
      { label: 'ST Pop',      field: 'st' },
      { label: 'ST %',        field: 'st_pct' },
      { label: 'SC Pop',      field: 'sc' },
      { label: 'SC %',        field: 'sc_pct' },
      { label: 'Total Pop',   field: 'total' },
    ],
  },
  v40_sc: {
    title: '40%+ SC Population Villages',
    columns: [
      { label: 'District',    field: 'district', stateOnly: true },
      { label: 'Block',       field: 'block' },
      { label: 'Village',     field: 'village' },
      { label: 'Habitations', field: 'habitations' },
      { label: 'SC Pop',      field: 'sc' },
      { label: 'SC %',        field: 'sc_pct' },
      { label: 'ST Pop',      field: 'st' },
      { label: 'ST %',        field: 'st_pct' },
      { label: 'Total Pop',   field: 'total' },
    ],
  },
  v40_st: {
    title: '40%+ ST Population Villages',
    columns: [
      { label: 'District',    field: 'district', stateOnly: true },
      { label: 'Block',       field: 'block' },
      { label: 'Village',     field: 'village' },
      { label: 'Habitations', field: 'habitations' },
      { label: 'ST Pop',      field: 'st' },
      { label: 'ST %',        field: 'st_pct' },
      { label: 'SC Pop',      field: 'sc' },
      { label: 'SC %',        field: 'sc_pct' },
      { label: 'Total Pop',   field: 'total' },
    ],
  },
  sc_habitations: {
    title: 'SC Habitations (SC > 40%)',
    columns: [
      { label: 'District',   field: 'district', stateOnly: true },
      { label: 'Block',      field: 'block' },
      { label: 'Village',    field: 'village' },
      { label: 'Habitation', field: 'habitation' },
      { label: 'SC Pop',     field: 'sc' },
      { label: 'SC %',       field: 'sc_pct' },
      { label: 'ST Pop',     field: 'st' },
      { label: 'ST %',       field: 'st_pct' },
      { label: 'General',    field: 'general' },
      { label: 'Total Pop',  field: 'total' },
    ],
  },
  st_habitations: {
    title: 'ST Habitations (ST > 40%)',
    columns: [
      { label: 'District',   field: 'district', stateOnly: true },
      { label: 'Block',      field: 'block' },
      { label: 'Village',    field: 'village' },
      { label: 'Habitation', field: 'habitation' },
      { label: 'ST Pop',     field: 'st' },
      { label: 'ST %',       field: 'st_pct' },
      { label: 'SC Pop',     field: 'sc' },
      { label: 'SC %',       field: 'sc_pct' },
      { label: 'General',    field: 'general' },
      { label: 'Total Pop',  field: 'total' },
    ],
  },
  st_sea_habitations: {
    title: 'ST Habitations (Socio-Economic Assessment)',
    source: '/habitation-sea-raw.json',
    columns: [
      { label: 'District',     field: 'district', stateOnly: true },
      { label: 'Block',        field: 'block' },
      { label: 'Village',      field: 'village' },
      { label: 'Habitation',   field: 'habitation' },
      { label: 'Households',   field: 'households' },
      { label: 'Members',      field: 'members' },
      { label: 'Male',         field: 'male' },
      { label: 'Female',       field: 'female' },
      { label: 'Third Gender', field: 'third_gender' },
    ],
  },
  dropouts: {
    title: 'Drop-outs — 2025-26',
    source: '/dropout-raw.json',
    columns: [
      { label: 'District',    field: 'district', stateOnly: true },
      { label: 'Block',       field: 'block' },
      { label: 'School',      field: 'school' },
      { label: 'Management',  field: 'management' },
      { label: 'Student',     field: 'student' },
      { label: 'Class',       field: 'class' },
      { label: 'Gender',      field: 'gender' },
      { label: 'Community',   field: 'community' },
      { label: 'Disability',  field: 'disability' },
      { label: 'Reason',      field: 'reason' },
      { label: 'Sub-reason',  field: 'sub_reason' },
      { label: 'Bucket',      field: 'bucket' },
      { label: 'Admit Status',field: 'admit_status' },
    ],
  },
  adw_schools: {
    title: 'ADW Schools',
    source: '/adw-schools-raw.json',
    columns: [
      { label: 'District', field: 'district', stateOnly: true },
      { label: 'Block',    field: 'block' },
      { label: 'School',   field: 'school' },
      { label: 'Category', field: 'category' },
      { label: 'Boys',     field: 'boys' },
      { label: 'Girls',    field: 'girls' },
      { label: 'Total',    field: 'total' },
    ],
  },
  tw_schools: {
    title: 'Tribal Welfare Schools',
    source: '/tw-schools-raw.json',
    columns: [
      { label: 'District', field: 'district', stateOnly: true },
      { label: 'School',   field: 'school' },
      { label: 'Category', field: 'category' },
      { label: 'Boys',     field: 'boys' },
      { label: 'Girls',    field: 'girls' },
      { label: 'Total',    field: 'total' },
    ],
  },
  school_results: {
    title: 'School Pass Results',
    dynamicSource: (stream) => `/school-results-${stream}.json`,
    columns: [
      { label: 'District',     field: 'district', stateOnly: true },
      { label: 'School',       field: 'school' },
      { label: 'Appeared',     field: 'appeared' },
      { label: 'Passed',       field: 'passed' },
      { label: 'Failed',       field: 'failed' },
      { label: 'Boys Pass %',  field: 'male_pct' },
      { label: 'Girls Pass %', field: 'female_pct' },
    ],
  },
};

// Per-source browser-tab cache — keyed by fetch URL
const dataCache = {};

const HabitationDetailModal = ({ kind, district, filter, filterLabel, stream, onClose }) => {
  const cfg = CONFIGS[kind];
  const url = cfg?.dynamicSource ? cfg.dynamicSource(stream) : (cfg?.source || '/habitation-raw.json');

  const [rawData, setRawData] = useState(dataCache[url] || null);
  const [loading, setLoading] = useState(!dataCache[url]);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (dataCache[url]) { setRawData(dataCache[url]); setLoading(false); return; }
    setLoading(true); setError('');
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        dataCache[url] = data;
        setRawData(data);
      })
      .catch(err => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [url]);

  const isStateView = !district;

  // Build the row list based on kind
  const rows = useMemo(() => {
    if (!rawData || !cfg) return [];
    const scope = district ? (rawData.districts[district] || {}) : rawData.state;
    // Two shapes:
    //  - Multi-kind (habitation-raw.json): scope is an object keyed by kind → array
    //  - Single-kind (habitation-sea-raw.json, dropout-raw.json): scope is already the array
    let list = Array.isArray(scope) ? scope : (scope[kind] || []);
    // Kind-specific post-filter
    if (kind === 'dropouts' && filter && filter !== 'all') {
      const needle = { adw: 'adi', tw: 'tribal', forest: 'forest' }[filter];
      if (needle) list = list.filter(r => (r.management || '').toLowerCase().includes(needle));
    }
    if ((kind === 'adw_schools' || kind === 'tw_schools') && filter) {
      if (filter === 'running') list = list.filter(r => (r.total || 0) > 0);
      else if (filter === 'zero') list = list.filter(r => (r.total || 0) === 0);
    }
    return list;
  }, [rawData, cfg, kind, district, filter, isStateView]);

  // Filter by search query
  const filtered = useMemo(() => {
    if (!q) return rows;
    const ql = q.toLowerCase();
    return rows.filter(r =>
      Object.values(r).some(v => String(v ?? '').toLowerCase().includes(ql))
    );
  }, [rows, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [q, kind]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);

  const visibleCols = cfg?.columns.filter(c => !c.stateOnly || isStateView) || [];

  const exportCSV = () => {
    const header = ['S.No.', ...visibleCols.map(c => c.label)].join(',');
    const rowsCSV = filtered.map((r, i) =>
      [i + 1, ...visibleCols.map(c => {
        const v = r[c.field];
        const s = v == null ? '' : String(v);
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      })].join(',')
    );
    const csv = [header, ...rowsCSV].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${cfg.title.replace(/\s+/g, '_')}_${district || 'All'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!cfg) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-[#0f2e4d] text-white">
          <div>
            <h2 className="text-lg font-bold">{cfg.title}{filterLabel ? ` — ${filterLabel}` : ''}</h2>
            <p className="text-xs text-blue-200 mt-0.5">
              {district ? <>District: <strong className="text-white">{district}</strong></> : 'All Districts (Tamil Nadu)'} · {filtered.length.toLocaleString('en-IN')} rows
            </p>
          </div>
          <button onClick={onClose} className="text-blue-100 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Filter + Export */}
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3 bg-slate-50">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 border border-gray-300 focus:outline-none focus:border-[#0f2e4d] text-sm bg-white"
            />
          </div>
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0f2e4d] text-white text-xs font-semibold hover:bg-[#1a3f66] disabled:opacity-40"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>

        {/* Table body */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="text-center py-16 text-sm text-gray-500">Loading data…</div>
          )}
          {error && !loading && (
            <div className="text-center py-8 text-sm text-red-700">Failed to load: {error}</div>
          )}
          {!loading && !error && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-gray-600 font-semibold w-12">S.No.</th>
                  {visibleCols.map(c => (
                    <th key={c.field} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-600 font-semibold">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.length === 0 ? (
                  <tr><td colSpan={visibleCols.length + 1} className="text-center py-16 text-sm text-gray-400">No rows.</td></tr>
                ) : (
                  pageRows.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-3 py-2 text-gray-500 font-medium">{start + i + 1}</td>
                      {visibleCols.map(c => (
                        <td key={c.field} className="px-4 py-2 text-gray-800">
                          {c.field.endsWith('_pct') || c.field === 'sc_max_pct' || c.field === 'st_max_pct'
                            ? (r[c.field] != null ? `${Number(r[c.field]).toFixed(2)}%` : '—')
                            : (r[c.field] != null && r[c.field] !== '' ? new Intl.NumberFormat('en-IN').format(r[c.field]).replace(/^NaN$/, r[c.field]) : '—')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && !error && filtered.length > PAGE_SIZE && (
          <div className="border-t border-gray-200 px-6 py-2 bg-slate-50 flex items-center justify-between text-xs">
            <span className="text-gray-600">
              Showing <strong>{start + 1}</strong>–<strong>{Math.min(start + PAGE_SIZE, filtered.length)}</strong> of <strong>{filtered.length.toLocaleString('en-IN')}</strong>
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)}                     disabled={page === 1}          className="px-3 py-1 text-gray-700 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40">First</button>
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}          className="px-3 py-1 text-gray-700 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40">Prev</button>
              <span className="px-3 py-1 text-gray-700">Page <strong>{page}</strong> of <strong>{totalPages}</strong></span>
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} className="px-3 py-1 text-gray-700 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40">Next</button>
              <button onClick={() => setPage(totalPages)}                    disabled={page === totalPages} className="px-3 py-1 text-gray-700 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40">Last</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HabitationDetailModal;
